import { Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { Outlet } from 'react-router-dom';

import { Button } from '@/components/Button';
import { useAlertsStore, useUserStore } from '@/configs/store';
import { useLocalStore } from '@/hooks/useLocalStore';
import { trackerUrls } from '@/tracker/config/urls';
import { useTrackerStore } from '@/tracker/model';
import { Loader } from '@/tracker/ui/Loader';
import { PageNav } from '@/tracker/ui/PageNav';

import { BoardHeader } from '../BoardHeader';
import { BoardLayoutStore } from '../../model/BoardLayoutStore';

import s from './BoardLayout.module.scss';

/** Доска: шапка с поиском и вкладками остаётся на месте, под ней меняются «Бэклог» и «Доска» */
const BoardLayout = observer(({ boardId }: { boardId: string }) => {
  const alertsStore = useAlertsStore();
  const trackerStore = useTrackerStore();
  const { user, isAdmin } = useUserStore();
  const { store } = useLocalStore(
    () =>
      new BoardLayoutStore({
        alertsStore,
        trackerStore,
        boardId,
        userId: user?.id ?? null,
        isAdmin,
      }),
  );

  useEffect(() => {
    store.load();
  }, [store]);

  const { board } = store;

  const navigation = (
    <PageNav parent={{ label: 'Доски', to: trackerUrls.boards }} current="Доска" />
  );

  if (store.isNotFound) {
    return (
      <div className={s.page}>
        {navigation}
        <div className={s.page__empty}>
          <Typography color="text.secondary">Доска не найдена</Typography>
        </div>
      </div>
    );
  }

  if (store.isError) {
    return (
      <div className={s.page}>
        {navigation}
        <div className={s.page__empty}>
          <Typography color="text.secondary">Не удалось загрузить доску</Typography>
          <Button onClick={store.load}>Повторить</Button>
        </div>
      </div>
    );
  }

  if (!board) return <Loader />;

  return (
    <div className={s.page}>
      <BoardHeader
        board={board}
        query={store.query}
        canManage={store.canManage}
        onQueryChange={store.setQuery}
        onBoardChange={store.setBoard}
      />
      <Outlet context={store} />
    </div>
  );
});

export { BoardLayout };
